import { useCallback, useEffect, useMemo, useState } from "react";

type UsePaginationOpts = {
  pageSize?: number;
  resetOnDataChange?: boolean;
};

export function usePagination<T>(
  data: T[],
  { pageSize = 10, resetOnDataChange = false }: UsePaginationOpts = {}
) {
  const [page, setPage] = useState(1);

  // total de páginas (al menos 1)
  const totalPages = Math.max(1, Math.ceil((data?.length || 0) / pageSize));

  // si cambia el tamaño de data:
  useEffect(() => {
    if (resetOnDataChange) {
      setPage(1);
    } else {
      // clamp si la página actual quedó fuera de rango
      setPage((p) => Math.min(Math.max(p, 1), totalPages));
    }
  }, [data, totalPages, resetOnDataChange]);

  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  const items = useMemo(() => data.slice(start, end), [data, start, end]);

  const next = useCallback(() => {
    setPage((p) => Math.min(p + 1, totalPages));
  }, [totalPages]);

  const prev = useCallback(() => {
    setPage((p) => Math.max(p - 1, 1));
  }, []);

  const canNext = page < totalPages;
  const canPrev = page > 1;

  const goTo = useCallback(
    (p: number) => {
      setPage(Math.min(Math.max(p, 1), totalPages));
    },
    [totalPages]
  );

  const showPagination = useMemo(() => {
    return canNext || canPrev;
  }, [canNext, canPrev]);

  return {
    page,
    totalPages,
    items,
    next,
    prev,
    canNext,
    canPrev,
    goTo,
    setPage,
    showPagination,
  };
}
